import React, { useState, useEffect } from 'react';
import DollarV2 from '../../../assets/SVGs/DollarV2';

interface DonationAmountSelectorProps {
  amount: number;
  setAmount: (amount: number) => void;
}

const DonationAmountSelector: React.FC<DonationAmountSelectorProps> = ({ amount, setAmount }) => {
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isCustomMode, setIsCustomMode] = useState(false);

  const presetAmounts = [25, 50, 100, 250, 500];

  // Sync custom amount input when amount prop changes
  useEffect(() => {
    if (amount > 0) {
      setCustomAmount(amount.toString());
      // Check if it's a preset amount
      if (presetAmounts.includes(amount)) {
        setIsCustomMode(false);
      } else {
        setIsCustomMode(true);
      }
    }
  }, [amount]);

  const handlePresetClick = (presetAmount: number) => {
    setAmount(presetAmount);
    setIsCustomMode(false);
    setCustomAmount(presetAmount.toString());
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomAmount(value);
    
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    setCustomAmount(numericValue);
    
    // Update the main amount if it's a valid number
    const parsedAmount = parseFloat(numericValue);
    if (!isNaN(parsedAmount) && parsedAmount > 0) {
      setAmount(parsedAmount);
    } else {
      setAmount(0);
    }
  };

  const handleCustomInputFocus = () => {
    setIsCustomMode(true);
    if (amount > 0) {
      setCustomAmount(amount.toString());
    }
  };

  const isPresetSelected = (presetAmount: number) => {
    return amount === presetAmount;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 w-full">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-gray-500 font-bold text-3xl">
            <DollarV2 width={32} height={32} />
          </span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800">Donation Amount</h3>
          <p className="text-sm text-gray-500">Choose an amount to support our community</p>
        </div>
      </div>

      {/* Preset Amounts */}
      <div className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {presetAmounts.map((presetAmount) => (
            <button
              key={presetAmount}
              onClick={() => handlePresetClick(presetAmount)}
              className={`px-4 py-3 rounded-lg font-semibold text-gray-800 transition-all duration-200 ${
                isPresetSelected(presetAmount)
                  ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                  : 'bg-gray-100 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              ${presetAmount}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Amount */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Or enter custom amount</h4>
        <div className="relative">
          <input
            type="number"
            value={customAmount}
            onChange={(e) => {
              if (!/^\d*$/.test(e.target.value)) return;
              const value = Number(e.target.value);
              if (value >= 0 && value <= 10000) {
                handleCustomAmountChange(e);
              }
            }}
            onFocus={handleCustomInputFocus}
            placeholder="$ 0"
            min={0}
            max={10000}
            // @ts-ignore
            onWheel={(e) => e?.target?.blur()}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            className="w-full px-4 py-3 border-0 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-lg font-medium text-gray-700 bg-[#F8F2F4]"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationAmountSelector; 